import { runtimeConfig } from "config/runtimeConfig";
import React, { useEffect, useState } from "react";

import AuthManager from "../../../Common/AuthManager";
import ServiceWrapper from "../../../Services/ServiceWrapper";
import SidePanel from "Components/DesignSystem/SidePanel/SidePanel";
import IconButton from "Components/DesignSystem/IconButton/IconButton";
import Typography from "Components/DesignSystem/Typography/Typography";
import Skeleton from "Components/DesignSystem/Skeleton/Skeleton";
import EmptyState from "Components/DesignSystem/EmptyState/EmptyState";
import { Cross, Refresh } from "Components/DesignSystem/Iconography/Icons";
import { toast } from "Components/DesignSystem/Toast/useToast";

const API_URL = runtimeConfig.GMS_BACKEND_API_URL;

/*
    Display-only typesetting, shared shape with CatalogManagement's
    ReviewQueueLogModal (2026-09-05): the BE writes "<timestamp> UTC: <message>",
    and repeating that prefix at full body weight is what made the panel read as
    a wall. Lazy `.+?` stops at the FIRST " UTC: "; a non-matching line renders
    verbatim — log content is never altered. If a THIRD consumer appears, this
    earns a DS component instead of a second copy.
*/
const LOG_LINE = /^(.+?\bUTC)\s*:\s*([\s\S]*)$/;

/**
 * The rule-queue log drawer on the DS `SidePanel` (§ 109).
 *
 * It was a `<div className="ps-0 tw-z-50 form-header">`, and `form-header` is a
 * **global** class that was defined twice: `Admin/connections/Connection.css` and this
 * module's own `RulesLog.css` — a copy-paste of it, both 118 lines, both defining the
 * same 16 class names, differing by exactly two lines, both inside `.form-header`
 * (`right: -25px` vs `1px`, `overflow-y: scroll` vs `auto`).
 *
 * **`RulesLog.css` is DELETED as of 2026-08-27, and it was a no-op.** Measured in the
 * built bundle with the import still in place: Connection.css's rule emitted at byte
 * 283215 and RulesLog.css's at 267420, so Connection.css came last and won — `right:
 * 1px` was already in effect and the copy's 25px of off-screen overhang was dead CSS
 * that never applied to anything, here or for `.form-header`'s four remaining consumers
 * (`ConfigurationPanel`, `ConnectionLogs`, `DataImportForm`, `ARS/ARSLogs`). The bundle
 * after deletion carries one `.form-header` rule, `right: 1px`, unchanged.
 *
 * That the harmless copy was the loser was luck, not design: the winner was decided by
 * import-graph order, and nothing in either file hinted the other existed. Any refactor
 * that reordered those imports would have flipped it and moved four unrelated drawers
 * 26px sideways. `SidePanel` ends the sharing for this component: the panel positions
 * itself and needs no global class.
 *
 * What the drawer gains, all from the shared component rather than added here: a
 * `role="dialog"` with an accessible name (it had none), Escape and backdrop
 * dismissal, focus moved in on open and handed back to the trigger on close, and a
 * body scroll lock. Stacking over the Rules Queue panel is safe and deliberate —
 * `useDismissable` keeps a separate LIFO stack for modal layers, so Escape and the Tab
 * cycle belong to whichever panel is on top, and the scroll lock is reference-counted
 * so closing the log does not unlock the page while the queue is still open.
 *
 * Behaviour deliberately preserved to the letter: the `logData` prop is split on blank
 * lines and REVERSED (newest first); the refresh button re-fetches
 * `rule_queue/{logId}` with no artificial delay and toasts "Log fetch failed." on
 * `done: false`; and `setViewLog(false)` is what closes it.
 */
const RulesLogs = ({ logData, setViewLog, logId }) => {
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (logData) {
      const sortedLogs = logData?.split("\n\n").reverse() ?? [];
      setLog(sortedLogs);
    }
  }, [logData]);

  const fetchLog = async () => {
    setLoading(true);

    let res = await ServiceWrapper.doGet(
      `${API_URL}rule_queue/${logId}`,
      null,
      null,
      AuthManager.getGmsToken()
    );

    if (res?.data?.done) {
      const logsString = res.data.data?.logs;
      const sortedLogs = logsString?.split("\n\n").reverse() ?? [];
      setLog(sortedLogs);
    } else {
      toast.error("Log fetch failed.");
    }

    setLoading(false);
  };

  return (
    <SidePanel
      isOpen
      onClose={() => setViewLog(false)}
      width="md"
      ariaLabel="Logs"
      title="Logs"
      tabs={[]}
      /* `headerActions` is REQUIRED here, not decoration: omitted, SidePanel falls
         back to its legacy Cancel/Save pair (§ 53), and this panel has nothing to
         save. Refresh moved from beside the title into this slot (2026-09-05) so
         the header matches the Review Queue log's Refresh+X pair; both are § 212
         IconButtons now, testids and accessible names unchanged. */
      headerActions={
        <>
          <IconButton
            size="sm"
            aria-label="Refresh logs"
            data-testid="rules-log-refresh-btn"
            disabled={loading}
            onClick={() => fetchLog()}
          >
            <Refresh className={loading ? "tw-animate-spin" : undefined} />
          </IconButton>
          <IconButton
            size="sm"
            aria-label="Close logs"
            data-testid="rules-ruleslog-button-1"
            onClick={() => setViewLog(false)}
          >
            <Cross size={16} />
          </IconButton>
        </>
      }
      data-testid="rules-log-panel"
    >
      <div className="tw-w-full">
        {loading ? (
          <div className="tw-p-3 tw-border-b tw-border-edge" data-testid="rules-log-loading">
            <Skeleton variant="text" />
          </div>
        ) : log.length === 0 ? (
          /* The legacy panel rendered an empty white rectangle when a queue row had no
             logs yet — no explanation, indistinguishable from a failed fetch. */
          <EmptyState
            title="No logs yet"
            size="sm"
            data-testid="rules-log-empty"
          />
        ) : (
          log.map((logitem, index) => {
            const m = LOG_LINE.exec(logitem);
            return (
              <div
                className="tw-p-3 tw-w-full tw-border-b tw-border-edge"
                key={index}
                data-testid={`rules-log-entry-${index}`}
              >
                {m ? (
                  <>
                    <Typography
                      variant="caption-metadata"
                      as="div"
                      className="tw-text-ink-tertiary tw-tabular-nums"
                    >
                      {m[1]}
                    </Typography>
                    <Typography
                      variant="body-primary"
                      as="p"
                      className="tw-text-ink tw-break-words tw-mt-0.5"
                    >
                      {m[2]}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body-primary" as="p" className="tw-break-words">
                    {logitem}
                  </Typography>
                )}
              </div>
            );
          })
        )}
      </div>
    </SidePanel>
  );
};

export default RulesLogs;
